import React, { useCallback, useEffect, useState, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import { AnimatePresence, LayoutGroup, motion } from 'framer-motion';
import { Loader } from 'lucide-react';
import FileCard from './FileCard';
import { RootState } from '../../../../reducers';
import { toggleSelectFile, fetchFiles } from '../../librarySlice';
import { FileType } from '../../../../types';
import { RouteParams } from '../../types';
import { getFileUrl } from '../../../../lib/routingUtils';
import PendingUploads from '../../../upload/PendingUploads';
import FilesSkeleton from './FilesSkeleton';
import FilesEmpty from '../FilesEmpty';
import { useQuery } from '../../../../hooks/urlHooks';
import { useSelectMode } from '../../context/SelectModeContext';
import useDebounce from '../../../../hooks/useDebounce';
import RecoveryFileCard from '../RecoveryFileCard';
import { loadBackupRecordsThunk } from '../../../settings/settingsSlice';
import { useMediaQuery } from '../../../../hooks/use-media-query';

export const FileGridView = React.memo(() => {
  const {
    library,
    upload,
    import: importState,
  } = useSelector((state: RootState) => state);
  const { activeUploads } = upload;
  const dispatch = useDispatch();
  const { teamId, folderId } = useParams<RouteParams>();
  const { searchQuery, sortBy = 'createdAt', sortOrder = 'desc' } = useQuery();
  const { files } = library;
  const { selectedFiles } = library;
  const { selectMode } = useSelectMode();
  const showCheckbox = selectMode || Object.keys(selectedFiles).length > 0;
  const { status } = files;
  const [isNearBottom, setIsNearBottom] = useState(false);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [loadingPage, setLoadingPage] = useState<number | null>(null);
  const scrollHandlerRef = useRef<(() => void) | null>(null);
  const [shouldLoadMore, setShouldLoadMore] = useState(false);
  const [preloadedPages, setPreloadedPages] = useState<Set<number>>(new Set());

  // Mobile detection moved to parent
  const isMobile = useMediaQuery('(max-width: 640px)');

  // Reduce debounce time for faster preloading
  const debouncedShouldLoadMore = useDebounce(shouldLoadMore, 50);

  const onFileSelect = useCallback(
    (file: FileType) => {
      const isCurrentlySelected = !!selectedFiles[file._id];

      dispatch(toggleSelectFile(file));

      // If chat panel is open, add or remove file from chat context
      if ((window as any).__chatPanelControls?.isOpen?.()) {
        if (isCurrentlySelected) {
          // File is being deselected, remove from chat
          (window as any).__chatContextControls?.removeFileFromContext?.(
            file._id,
          );
        } else {
          // File is being selected, add to chat
          (window as any).__chatContextControls?.addFileToContext?.(file);
        }
      }
    },
    [dispatch, selectedFiles],
  );

  const handleScroll = useCallback(
    (event?: Event) => {
      // Try to find the scrollable container - could be window or a custom container
      const { documentElement } = document;
      let { scrollHeight, scrollTop, clientHeight } = documentElement;

      // If we have an event, try to use the target if it's scrollable
      if (event && event.target && event.target !== document) {
        const target = event.target as Element;
        if (target.scrollHeight > target.clientHeight) {
          ({ scrollHeight, scrollTop, clientHeight } = target);
        }
      }

      // Also check for common scrollable containers (including MUI Box) - OPTIMIZED: Only query once if possible or rely on specific class
      // For now, simplifying to just window and the main known container if it exists
      // Finding scrollable container by ID or simpler class is better than scanning all styles
      const scrollableContainer = document.getElementById(
        'main-scroll-container',
      );

      if (scrollableContainer) {
        ({ scrollHeight, scrollTop, clientHeight } = scrollableContainer);
      } else if (scrollHeight <= clientHeight) {
        // Fallback to detecting the first scrollable element among common candidates (only if window isn't scrolling)
        // This is still expensive but safer than searching *all* elements
        const candidate = document.querySelector(
          '.overflow-y-auto, .overflow-auto',
        );
        if (candidate) {
          ({ scrollHeight, scrollTop, clientHeight } = candidate);
        }
      }

      const distanceFromBottom = scrollHeight - scrollTop - clientHeight;
      // Preload when user is 2 viewport heights away from bottom for seamless scrolling
      const preloadThreshold = window.innerHeight * 2;
      // Show skeleton only when very close to bottom (half viewport)
      const skeletonThreshold = window.innerHeight * 0.5;

      // Update near-bottom state for skeleton display (much closer to bottom)
      setIsNearBottom(distanceFromBottom < skeletonThreshold);

      if (
        distanceFromBottom < preloadThreshold &&
        files.status !== 'pending' &&
        !isLoadingMore &&
        files.pagination.total > files.data.length
      ) {
        const nextPage = files.pagination.page + 1;
        const maxPages = Math.ceil(
          files.pagination.total / files.pagination.limit,
        );

        // Prevent duplicate page requests - only load if we haven't already loaded this page
        if (
          loadingPage === nextPage ||
          nextPage > maxPages ||
          preloadedPages.has(nextPage)
        ) {
          return;
        }

        // Trigger the debounced load instead of loading immediately
        setShouldLoadMore(true);
      } else {
        setShouldLoadMore(false);
      }
    },
    [files, isLoadingMore, loadingPage],
  );

  // Effect to handle the actual API call when debounced trigger fires
  useEffect(() => {
    if (debouncedShouldLoadMore && !isLoadingMore) {
      const nextPage = files.pagination.page + 1;
      const maxPages = Math.ceil(
        files.pagination.total / files.pagination.limit,
      );

      if (
        loadingPage !== nextPage &&
        nextPage <= maxPages &&
        !preloadedPages.has(nextPage)
      ) {
        setIsLoadingMore(true);
        setLoadingPage(nextPage);
        setPreloadedPages((prev) => new Set([...prev, nextPage]));
        dispatch(
          fetchFiles({
            teamId,
            folderId,
            page: nextPage,
            limit: files.pagination.limit, // Use the same limit as in pagination
            searchQuery,
            sortBy,
            sortOrder,
          }),
        );
      }
    }
  }, [
    debouncedShouldLoadMore,
    files,
    dispatch,
    teamId,
    folderId,
    searchQuery,
    sortBy,
    sortOrder,
    isLoadingMore,
    loadingPage,
  ]);

  useEffect(() => {
    // Reset loading state when dependencies change
    setIsLoadingMore(false);
    setLoadingPage(null);
    setPreloadedPages(new Set());
  }, [teamId, folderId, searchQuery, sortBy, sortOrder]);

  useEffect(() => {
    // Reset loading state when files fetch completes
    if (files.status !== 'pending') {
      setIsLoadingMore(false);
      setLoadingPage(null);
    }
  }, [files.status]);

  // Load backup records for recovery detection
  useEffect(() => {
    dispatch(loadBackupRecordsThunk());
  }, [dispatch]);

  useEffect(() => {
    // Store the handler reference for cleanup
    scrollHandlerRef.current = handleScroll;

    // Listen for scroll on window/document
    window.addEventListener('scroll', handleScroll, { passive: true });

    // Listen on specific known scroll container
    const scrollableContainer =
      document.getElementById('main-scroll-container') ||
      document.querySelector('.overflow-y-auto');
    if (scrollableContainer) {
      scrollableContainer.addEventListener('scroll', handleScroll, {
        passive: true,
      });
    }

    return () => {
      window.removeEventListener('scroll', handleScroll);
      if (scrollableContainer) {
        scrollableContainer.removeEventListener('scroll', handleScroll);
      }

      // Also remove with the ref in case the handler changed
      if (scrollHandlerRef.current) {
        window.removeEventListener('scroll', scrollHandlerRef.current);
      }
    };
  }, [handleScroll]);

  if (!teamId || !folderId) {
    return null;
  }

  // Show skeleton on initial load or when fetching
  const showSkeleton = status === 'pending';

  // Only show empty state after successful load with no results
  if (
    status === 'succeeded' &&
    files.data?.length === 0 &&
    activeUploads.length === 0 &&
    importState.status !== 'pending'
  ) {
    return <FilesEmpty />;
  }

  // Show skeleton while loading (prevents empty state flash on initial load)
  if (status === 'pending' && files.data.length === 0) {
    return (
      <div
        className="grid gap-4 min-h-[200px]"
        style={{
          gridTemplateColumns: 'repeat(auto-fill, minmax(250px, 1fr))',
        }}
      >
        <FilesSkeleton />
      </div>
    );
  }
  const showPaginationLoader =
    isLoadingMore &&
    isNearBottom &&
    files.pagination.total > files.data.length &&
    status !== 'pending';

  return (
    <LayoutGroup>
      <div>
        <motion.div
          layout
          className="grid gap-4 min-h-[200px]"
          style={{
            gridTemplateColumns: 'repeat(auto-fill, minmax(250px, 1fr))',
          }}
        >
          <PendingUploads view="grid" />

          {/* Recovery File Card - Shows when there are recent recoverable files */}
          <RecoveryFileCard />

          <AnimatePresence mode="popLayout">
            {files.data.map((file) => {
              const isSelected = !!selectedFiles[file._id];
              return (
                <FileCard
                  key={file._id}
                  file={file}
                  isSelected={isSelected}
                  showCheckbox={showCheckbox}
                  onSelect={onFileSelect}
                  fileUrl={getFileUrl({
                    teamId,
                    parentId: folderId,
                    fileId: file._id,
                    withBasePath: false,
                  })}
                  isMobile={isMobile}
                />
              );
            })}
          </AnimatePresence>
          {showSkeleton && <FilesSkeleton />}
        </motion.div>
        {showPaginationLoader && (
          <div className="flex items-center justify-center py-8">
            <Loader className="h-6 w-6 animate-spin text-muted-foreground" />
          </div>
        )}
      </div>
    </LayoutGroup>
  );
});

export default FileGridView;
